/**
 * Shared building blocks for the admin dashboard tabs.
 *
 * Reusable bits that multiple tabs render:
 *   - KpiCard:            metric tile with icon, label, value, subtext
 *   - BarViz:             horizontal bar list with progress fills
 *   - DailyActivityChart: 30-day activity sparkline
 *   - LoadingSkeleton:    KPI + chart placeholder while data loads
 */
import React from 'react';
import { Activity } from 'lucide-react';

export function KpiCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <div
      className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-xs font-medium text-gray-500">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  );
}

export function BarViz({ items, maxVal, color = '#14b8a6' }) {
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.label}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-700 truncate mr-2">{item.label}</span>
            <span className="text-gray-500 font-medium whitespace-nowrap">{item.value}</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.max((item.raw / maxVal) * 100, 2)}%`, backgroundColor: color }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function DailyActivityChart({ data, label }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 text-teal-500" /> {label}
        </h3>
        <p className="text-gray-400 text-sm">No real-time data yet. Events will appear as users browse the site.</p>
      </div>
    );
  }
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="daily-activity-chart">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-teal-500" /> {label}
      </h3>
      <div className="flex items-end gap-[3px] h-32">
        {data.map((d) => {
          const pct = Math.max((d.value / maxVal) * 100, 4);
          return (
            <div key={d.date} className="flex-1 group relative flex flex-col items-center justify-end h-full">
              <div
                className="w-full rounded-t transition-all duration-300 group-hover:opacity-80"
                style={{ height: `${pct}%`, backgroundColor: d.color || '#14b8a6', minHeight: '2px' }}
              />
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                {d.date}: {d.value}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-[10px] text-gray-400">{data[0]?.date}</span>
        <span className="text-[10px] text-gray-400">{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['k1', 'k2', 'k3', 'k4'].map(k => <div key={k} className="bg-white rounded-xl h-28 border border-gray-100" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl h-64 border border-gray-100" />
        <div className="bg-white rounded-xl h-64 border border-gray-100" />
      </div>
    </div>
  );
}
